import { createStore } from "zustand";

import type {
	FlashcardSet,
	FlashcardSetInfo,
	FlashcardSetPopulated,
	FlashcardSetRepetition,
	Share,
} from "@vita-learning/types";

import {
	createFlashcardSet,
	createFlashcardsShareUrl,
	createManyFlashcardSets,
	deleteFlashcardSet,
	fetchFlashcardSets,
	getFlashcardSet,
	shareFlashcardSet,
	sortFlashcardSetsByCompletion,
	updateFlashcardSet,
	updateFlashcardSetRepetition,
} from "~/shared/services";
import { partition } from "~/shared/utils";

export interface FlashcardSetState {
	readonly data: FlashcardSetPopulated[];
	readonly entity?: FlashcardSet | FlashcardSetPopulated;
	readonly isPending: boolean;
}
interface FlashcardSetRemoveOptions {
	/**
	 * If true, performs a soft delete without removing the flashcard set from the store.
	 */
	readonly soft?: boolean;
}

interface FlashcardSetActions {
	readonly get: (id: string) => Promise<FlashcardSetPopulated>;
	readonly set: (
		flashcardSet: FlashcardSet | ((prev: FlashcardSet) => FlashcardSet)
	) => void;
	readonly refresh: (url: string) => Promise<FlashcardSetPopulated[]>;
	readonly create: (
		flashcardSet: FlashcardSetInfo | FlashcardSet
	) => Promise<FlashcardSetPopulated>;
	readonly createMany: (
		flashcardSets: (FlashcardSetInfo | FlashcardSet)[]
	) => Promise<FlashcardSetPopulated[]>;
	readonly createShareUrl: (id: string) => Promise<string>;
	readonly update: (
		flashcardSet: FlashcardSet
	) => Promise<FlashcardSetPopulated>;
	readonly updateRepetition: (
		id: string,
		repetition: FlashcardSetRepetition
	) => Promise<FlashcardSetRepetition>;
	readonly share: (id: string, share: Share) => Promise<FlashcardSetPopulated>;
	readonly remove: (
		id: string,
		options?: FlashcardSetRemoveOptions
	) => Promise<FlashcardSetPopulated>;
	readonly edit: (id: string) => void;
	readonly reset: () => void;
}

export type FlashcardSetsStore = FlashcardSetState & FlashcardSetActions;

const defaultState: FlashcardSetState = {
	data: [],
	isPending: false,
};

export const createFlashcardSetsStore = (initialState: FlashcardSetState) =>
	createStore<FlashcardSetsStore>((set, get) => ({
		...defaultState,
		...initialState,
		refresh: async url => {
			try {
				set({ isPending: true });
				const flashcardSets = await fetchFlashcardSets(url, {
					cache: "no-cache",
				});
				const sortedFlashcardSets = flashcardSets.toSorted(
					sortFlashcardSetsByCompletion
				);

				set({ data: sortedFlashcardSets });
				return sortedFlashcardSets;
			} finally {
				set({ isPending: false });
			}
		},
		set: flashcardSetOrUpdater => {
			set(state => {
				if (!state.entity) return state;

				const nextEntity =
					typeof flashcardSetOrUpdater === "function"
						? flashcardSetOrUpdater(state.entity)
						: flashcardSetOrUpdater;
				return {
					...state,
					entity: nextEntity,
				};
			});
		},
		create: async flashcardSetInfo => {
			try {
				set({ isPending: true });
				const flashcardSet = await createFlashcardSet(flashcardSetInfo);

				set(state => ({
					...state,
					data: [...state.data, flashcardSet].toSorted(
						sortFlashcardSetsByCompletion
					),
				}));
				return flashcardSet;
			} finally {
				set({ isPending: false });
			}
		},
		share: async (id, share) => {
			try {
				set({ isPending: true });
				const sharedFlashcardSet = await shareFlashcardSet(id, share);

				set(state => ({
					...state,
					entity: state.entity ? sharedFlashcardSet : state.entity,
					data: state.data
						.map(f => (f.id !== sharedFlashcardSet.id ? f : sharedFlashcardSet))
						.toSorted(sortFlashcardSetsByCompletion),
				}));
				return sharedFlashcardSet;
			} finally {
				set({ isPending: false });
			}
		},
		createMany: async flashcardSets => {
			try {
				set({ isPending: true });

				const newFlashcardSets = await createManyFlashcardSets(flashcardSets);

				set(state => ({
					...state,
					data: [...state.data, ...newFlashcardSets],
				}));

				return newFlashcardSets;
			} finally {
				set({ isPending: false });
			}
		},
		createShareUrl: async id => {
			try {
				set({ isPending: true });
				const { url } = await createFlashcardsShareUrl({ id });

				return url;
			} finally {
				set({ isPending: false });
			}
		},
		get: async id => {
			try {
				set({ isPending: true });

				const flashcardSet = await getFlashcardSet(id, { cache: "no-cache" });

				return flashcardSet;
			} finally {
				set({ isPending: false });
			}
		},
		update: async flashcardSet => {
			try {
				set({ isPending: true });
				const updatedFlashcardSet = await updateFlashcardSet(flashcardSet);

				set(state => ({
					...state,
					entity: state.entity ? updatedFlashcardSet : state.entity,
					data: state.data
						.map(f =>
							f.id !== updatedFlashcardSet.id ? f : updatedFlashcardSet
						)
						.toSorted(sortFlashcardSetsByCompletion),
				}));
				return updatedFlashcardSet;
			} finally {
				set({ isPending: false });
			}
		},
		updateRepetition: async (id, repetition) => {
			try {
				set({ isPending: true });

				const updatedRepetition = await updateFlashcardSetRepetition(
					id,
					repetition
				);

				set(state => ({
					...state,
					data: state.data.map(f =>
						f.id !== id
							? f
							: {
									...f,
									repetitions: f.repetitions.map(r =>
										r.id !== updatedRepetition.id ? r : updatedRepetition
									),
								}
					),
					entity:
						state.entity && state.entity.id === id
							? {
									...state.entity,
									repetitions: state.entity.repetitions.map(r =>
										r.id !== updatedRepetition.id ? r : updatedRepetition
									),
								}
							: state.entity,
				}));

				return updatedRepetition;
			} finally {
				set({ isPending: false });
			}
		},
		remove: async (id, { soft = false } = { soft: false }) => {
			try {
				set({ isPending: true });
				await deleteFlashcardSet(id);

				const [[deletedFlashcardSet], restOfFlashcardSets] = partition(
					get().data,
					f => f.id === id
				);

				if (soft) return deletedFlashcardSet as FlashcardSetPopulated;

				set({ data: restOfFlashcardSets, entity: undefined });
				return deletedFlashcardSet as FlashcardSetPopulated;
			} finally {
				set({ isPending: false });
			}
		},
		edit: id => {
			set(state => ({
				...state,
				entity: state.data.find(f => f.id === id),
			}));
		},
		reset: () => set({ ...defaultState }),
	}));
